import { useEffect, useMemo, useRef, useState } from 'react'
import * as Y from 'yjs'
import { sourcesOf } from '../document/sectionDocument.js'
import { type CitationField, collectClusters } from './collectClusters.js'
import { createCitationEngine } from './engine.js'
import type { CitationResult } from './types.js'

const EMPTY: CitationResult = { inTekst: new Map(), literatuurlijst: [] }
const DELAY_MS = 150

/**
 * Herberekent het citatieresultaat van een sectie-instantie: het in-tekst-label per
 * `citatieId` en de literatuurlijst, uit de bronnenopslag en de citatieclusters in de
 * gegeven rich-text-velden (naar CitatenPOC's `useFormCitations`).
 *
 * **Vertraagd, niet per toetsaanslag.** Het zware werk (de documentwandeling plus citeproc)
 * hoort bij de vertraagde aanroep en niet bij elke wijziging, dezelfde afweging als in
 * CitatenPOC (150ms).
 *
 * **Eén engine per hook-instantie.** De stijl ligt vast (Harvard, zie `engine.ts`), dus er is geen
 * herbouwreden; `readSources` wijst via een ref altijd naar het huidige document.
 *
 * **`veldIds` moet stabiel zijn tussen renders** (bijvoorbeeld `useMemo` bij de aanroeper): een
 * nieuwe array-referentie bij elke render zou de subscriptie bij elke render opnieuw opzetten.
 *
 * Een bronwijziging zit al in de `doc`-subscriptie: `sourcesMap` is een gewone wortel van
 * hetzelfde document, dus `addSource` vuurt hetzelfde `update`-event als een tekstwijziging.
 */
export function useCitations(doc: Y.Doc | null, fieldIds: readonly CitationField[]): CitationResult {
  const [result, setResult] = useState<CitationResult>(EMPTY)
  const docRef = useRef(doc)
  docRef.current = doc

  const engine = useMemo(
    () => createCitationEngine(() => (docRef.current === null ? [] : sourcesOf(docRef.current))),
    [],
  )

  useEffect(() => {
    if (doc === null) {
      setResult(EMPTY)
      return
    }
    let timer: ReturnType<typeof setTimeout> | undefined
    const recompute = (): void => {
      if (timer !== undefined) clearTimeout(timer)
      timer = setTimeout(() => {
        setResult(engine.process(collectClusters(doc, fieldIds)))
      }, DELAY_MS)
    }
    recompute()
    doc.on('update', recompute)
    return () => {
      if (timer !== undefined) clearTimeout(timer)
      doc.off('update', recompute)
    }
  }, [doc, engine, fieldIds])

  return result
}
